import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { X } from 'lucide-react-native';
import { memo } from 'react';

import { KeyboardAwareActionsheet } from '@/components/KeyboardAwareActionsheet';
import { OnYourHonorReasonContent } from './OnYourHonorReasonContent';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from '@/components/ui/modal';
import { ON_YOUR_HONOR_COPY, type OnYourHonorReasonId } from '../../model/onYourHonor';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type OnYourHonorReasonSheetProps = {
  isOpen: boolean;
  /**
   * Rotating gratitude line once the mark has landed, or null while the question
   * is still open. Non-null swaps the body to the thank-you beat.
   */
  thanksLine: string | null;
  /** Record a predefined effort reason (single tap on a row). */
  onSelectReason: (id: OnYourHonorReasonId) => void;
  /** Record a free-text reason the expert typed via "Other". */
  onCustomReason: (text: string) => void;
  /** Dismiss without recording — wired to Skip, the X, backdrop, and Esc. */
  onSkip: () => void;
  /** The thank-you beat has elapsed; close the surface. */
  onThanksComplete: () => void;
};

/**
 * Native bottom sheet for the post-selection "above and beyond" reason survey,
 * on the shared `KeyboardAwareActionsheet` so the "Other" free-text field lifts
 * above the keyboard.
 *
 * Answering is the default path, but a tap on the backdrop, the X, or the
 * content's "Skip" dismisses without a reason — a survey that can't be escaped
 * is a usability/accessibility failure.
 */
function NativeOnYourHonorReasonSheet({
  isOpen,
  thanksLine,
  onSelectReason,
  onCustomReason,
  onSkip,
  onThanksComplete,
}: OnYourHonorReasonSheetProps) {
  return (
    <KeyboardAwareActionsheet
      isOpen={isOpen}
      onClose={onSkip}
      closeLabel={ON_YOUR_HONOR_COPY.reasonCloseA11yLabel}
      contentContainerClassName="px-5 pb-8">
      <OnYourHonorReasonContent
        thanksLine={thanksLine}
        onSelectReason={onSelectReason}
        onCustomReason={onCustomReason}
        onThanksComplete={onThanksComplete}
      />
    </KeyboardAwareActionsheet>
  );
}

/**
 * Platform-aware container for the post-selection "above and beyond" reason
 * survey: a bottom sheet on native (DoorDash-style review pattern), a
 * compact centered `Modal` on web. Answering is the default, but both are
 * escapable — Esc / backdrop on web, backdrop / Skip on native — because a modal
 * with no way out is an accessibility failure (APG dialog Esc, WCAG no keyboard
 * trap). A focused modal (rather than an inline card in the composer stack) is
 * deliberate: the prompt belongs on a layer that quiets the surrounding banners
 * rather than competing with them for the space above the composer. The shared
 * OnYourHonorReasonContent keeps the body identical across both surfaces.
 *
 * The web body's padding is load-bearing, not spacing. `ModalBody` is a
 * `ScrollView` and clips at its own content edge, so `pt-2` gives the badge room
 * for its entrance to overshoot without being shaved; the native sheet already
 * pads its scroll content. `pb-4` rather than `pb-6` because the header row
 * absorbs the top spacing for the X, which left the write-in floating.
 */
export const OnYourHonorReasonSheet = memo(function OnYourHonorReasonSheet({
  isOpen,
  thanksLine,
  onSelectReason,
  onCustomReason,
  onSkip,
  onThanksComplete,
}: OnYourHonorReasonSheetProps) {
  if (isWeb) {
    return (
      <Modal isOpen={isOpen} onClose={onSkip} size="sm">
        <ModalBackdrop />

        <ModalContent>
          <ModalHeader className="justify-end">
            <ModalCloseButton
              onPress={onSkip}
              {...getAccessibilityProps({
                role: 'button',
                label: ON_YOUR_HONOR_COPY.reasonCloseA11yLabel,
              })}>
              <Icon as={X} size="md" className="text-typography-500" />
            </ModalCloseButton>
          </ModalHeader>

          <ModalBody className="pb-4 pt-2">
            <OnYourHonorReasonContent
              thanksLine={thanksLine}
              onSelectReason={onSelectReason}
              onCustomReason={onCustomReason}
              onThanksComplete={onThanksComplete}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <NativeOnYourHonorReasonSheet
      isOpen={isOpen}
      thanksLine={thanksLine}
      onSelectReason={onSelectReason}
      onCustomReason={onCustomReason}
      onSkip={onSkip}
      onThanksComplete={onThanksComplete}
    />
  );
});

OnYourHonorReasonSheet.displayName = 'OnYourHonorReasonSheet';
